import { useEffect, useState, useRef } from 'react';
import { TimelineEvent } from '../../Timeline/Timeline';
import './EventModal.scss';
import { Icons } from '../../Icons/Icons';
import { Blurhash } from 'react-blurhash';
import useWindowDimensions from '../../../hooks/useWindowDimentions';
import {
  FacebookShareButton,
  TwitterShareButton,
  LinkedinShareButton,
} from 'react-share';

interface EventModalProps {
  event: TimelineEvent;
  isOpen: boolean;
  onClose: () => void;
  onEventOpen?: (event: TimelineEvent) => void;
  onEventClose?: () => void;
}

const EventModal: React.FC<EventModalProps> = ({ 
  event, 
  isOpen, 
  onClose,
  onEventOpen,
  onEventClose
}) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [lastTouchY, setLastTouchY] = useState<number | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const descriptionRef = useRef<HTMLDivElement>(null);
  const windowDimensions = useWindowDimensions();
  
  const shareUrl = window.location.href;
  const shareTitle = event.title;
  const shareText = `${event.title} (${event.year})`;

  const handleTouchStart = (e: React.TouchEvent) => {
    if (windowDimensions.width > 768) return;
    
    setTouchStart(e.touches[0].clientY);
    setLastTouchY(e.touches[0].clientY);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchStart || !lastTouchY || windowDimensions.width > 768) return;

    const touchY = e.touches[0].clientY;
    const currentDiff = lastTouchY - touchY;
    
    const descriptionElement = descriptionRef.current;
    
    if (descriptionElement) {
      const isScrollable = descriptionElement.scrollHeight > descriptionElement.clientHeight;
      const isAtTop = descriptionElement.scrollTop === 0;
      const isAtBottom = Math.abs(
        descriptionElement.scrollHeight - descriptionElement.clientHeight - descriptionElement.scrollTop
      ) < 1;

      if (isScrollable && 
          ((currentDiff > 0 && !isAtBottom) || (currentDiff < 0 && !isAtTop))) {
        e.stopPropagation();
      }

      if ((isAtTop && currentDiff < 0) || (isAtBottom && currentDiff > 0)) {
        e.preventDefault();
      }
    }

    setLastTouchY(touchY);
  };

  const handleTouchEnd = () => {
    setTouchStart(null);
    setLastTouchY(null);
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      onEventOpen?.(event);
    }
  }, [isOpen, event, onEventOpen]);

  const handleClose = () => {
    setIsAnimating(false);
    onEventClose?.();
    setTimeout(onClose, 700);
  };

  if (!isOpen) return null;

  return (
    <div 
      className={`event-modal ${isAnimating ? 'visible' : 'hidden'}`}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <button className="close-button" onClick={handleClose}>×</button>
      
      <div className="modal-content">
        <div className="modal-image">
          {event.blurhash && (
            <Blurhash
              hash={event.blurhash}
              width="100%"
              height="100%"
              resolutionX={32}
              resolutionY={32}
              punch={1}
            />
          )}
          <img 
            src={event.img} 
            alt={event.title}
            className={imageLoaded ? 'loaded' : ''}
            onLoad={() => setImageLoaded(true)}
          />
        </div>
        
        <div className="modal-info">
          <h1>{event.title}</h1>
          <div className="year">{event.year}</div>
          <div className="description" ref={descriptionRef}>
            {event.content}
          </div>
          
          <div className="share-section">
            <span>SHARE WITH:</span>
            <div className="social-links">
              <FacebookShareButton 
                url={shareUrl} 
                title={shareText}
                hashtag="#MuseumDigital"
                className="facebook"
              >
                <a role="button">{Icons.FacebookIcon}</a>
              </FacebookShareButton>
              <TwitterShareButton 
                url={shareUrl} 
                title={shareText} 
                hashtags={['MuseumDigital']}
                className="twitter"
              >
                <a role="button">{Icons.TwitterIcon}</a>
              </TwitterShareButton>
              <LinkedinShareButton 
                url={shareUrl} 
                title={shareTitle} 
                summary={shareText} 
                source="Museum Digital"
                className="linkedin"
              >
                <a role="button">{Icons.LinkedinIcon}</a>
              </LinkedinShareButton>
              <button 
                onClick={handleCopyLink} 
                className={`copy-link ${linkCopied ? 'copied' : ''}`}
              >
                <span className="copy-text">{linkCopied ? 'Copied!' : 'Copy Link'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventModal; 